import { useEffect } from "react";
import { SITE } from "@/config/site";
import { absUrl } from "@/lib/site";

const GSC = (process.env.REACT_APP_GSC_VERIFICATION || "").trim();

// React 19 hoists <title>, <meta> and <link> into <head> automatically.
export function Seo({ title, description, path = "/", image, noindex = false, type = "website" }) {
  const url = absUrl(path);
  const img = absUrl(image || SITE.ogImage);
  // Title + description update the static tags in index.html (avoids duplicate tags).
  useEffect(() => {
    document.title = title;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", description);
  }, [title, description]);
  return (
    <>
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large"} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.legalName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="en_ZA" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {GSC ? <meta name="google-site-verification" content={GSC} /> : null}
    </>
  );
}

export function JsonLd({ data }) {
  if (!data) return null;
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}

export const businessId = () => `${absUrl("/")}#business`;

export const businessLd = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": businessId(),
  name: SITE.name,
  legalName: SITE.legalName,
  description: "Women-led printing and branding business in Mitchells Plain, Cape Town offering custom T-shirt printing, branded clothing, uniforms, corporate gifts, personalised gifts and Xhosa cultural products.",
  url: absUrl("/"),
  logo: absUrl(SITE.logo),
  image: absUrl(SITE.ogImage),
  slogan: "Power of Dreams",
  telephone: SITE.phoneE164,
  email: SITE.email,
  foundingDate: String(SITE.foundingYear),
  founder: { "@type": "Person", name: SITE.founder },
  address: { "@type": "PostalAddress", addressLocality: `${SITE.locality}, ${SITE.city}`, addressRegion: SITE.region, addressCountry: SITE.countryCode },
  areaServed: [
    { "@type": "City", name: "Cape Town" },
    { "@type": "AdministrativeArea", name: "Western Cape" },
    { "@type": "Country", name: "South Africa" },
  ],
  currenciesAccepted: "ZAR",
  sameAs: SITE.social.map((s) => s.url),
  contactPoint: [{ "@type": "ContactPoint", telephone: SITE.phoneE164, contactType: "sales", email: SITE.email, areaServed: "ZA", availableLanguage: ["English", "isiXhosa"] }],
});

export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE.legalName,
  url: absUrl("/"),
  publisher: { "@id": businessId() },
  inLanguage: "en-ZA",
});

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [{ name: "Home", path: "/" }, ...items].map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: absUrl(it.path),
  })),
});

export const serviceLd = ({ name, description, path, products = [] }) => {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    serviceType: name,
    description,
    url: absUrl(path),
    provider: { "@id": businessId() },
    areaServed: [{ "@type": "City", name: "Cape Town" }, { "@type": "Country", name: "South Africa" }],
  };
  if (products.length) {
    ld.hasOfferCatalog = {
      "@type": "OfferCatalog",
      name,
      itemListElement: products.map((p) => {
        const offer = { "@type": "Offer", itemOffered: { "@type": "Product", name: p.name, description: p.desc } };
        if (p.price) offer.priceSpecification = { "@type": "PriceSpecification", minPrice: p.price, priceCurrency: "ZAR" };
        return offer;
      }),
    };
  }
  return ld;
};

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});
